import { BaseService } from './base';
import { ApiClient } from '../lib/client';
import { ApiRequest } from '../lib/request';
import { ApiListResponse } from '../lib/response';
import type { ApiResponse } from '../lib/response';
import { ApiException } from '../lib/exception';
import {
  Billing,
  BillingStatus,
  BillingPlan,
  BillingMethod,
  BillingPayment,
} from '../models/billing';
import { CustomerCreditCard } from '../models/customerCreditCard';

export class BillingService extends BaseService<Billing> {
  override model = Billing;

  override modelName = 'billing';
  override modelLabel = 'Billing';

  async status(): Promise<ApiResponse<BillingStatus>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/status`,
    });

    return await this.requestItem<BillingStatus>(request, BillingStatus);
  }

  async plans(): Promise<ApiListResponse<BillingPlan>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/plans`,
    });

    return await this.requestList<BillingPlan>(request, BillingPlan);
  }

  async methods(): Promise<ApiListResponse<BillingMethod>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/methods`,
    });

    return await this.requestList<BillingMethod>(request, BillingMethod);
  }

  async clientToken(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/clientToken`,
    });

    return await this.request(request);
  }

  async cancel(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/cancel`,
    });

    return await this.request(request);
  }

  async payments(page: number = 1): Promise<ApiListResponse<BillingPayment>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/payments`,
      query: { page: page.toString() },
    });

    const response = await ApiClient.request(request);

    if (response.success) {
      return new ApiListResponse<BillingPayment>(
        { ...response, data: response.data?.payments || [] },
        BillingPayment
      );
    }

    throw new ApiException(response);
  }

  async cancelReasons({
    reasons = [],
    requestCallback = false,
    notes = '',
    callbackDetails = '',
  }: {
    reasons?: string[];
    requestCallback?: boolean;
    notes?: string;
    callbackDetails?: string;
  }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/cancelReasons`,
      data: { reasons, requestCallback, notes, callbackDetails },
    });

    return await this.request(request);
  }

  async createMethod(nonce: string): Promise<ApiResponse<BillingMethod>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/methods`,
      data: { nonce },
    });

    return await this.requestItem<BillingMethod>(request, BillingMethod);
  }

  async setDefaultMethod({
    nonce,
    deviceData,
  }: {
    nonce: string;
    deviceData?: string;
  }): Promise<ApiResponse<BillingMethod>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/setDefaultMethod`,
      data: { nonce, deviceData },
    });

    return await this.requestItem<BillingMethod>(request, BillingMethod);
  }

  async deleteMethod(token: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: this.modelBaseUrl + '/methods',
      data: { token },
    });

    return await this.request(request);
  }

  async subscribe({
    plan,
    token,
    nonce,
    deviceData,
  }: {
    plan: string;
    token?: string;
    nonce?: string;
    deviceData?: string;
  }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/subscribe`,
      data: { plan, token, nonce, deviceData },
    });

    return await this.request(request);
  }

  async changePlan(plan: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/changePlan`,
      data: { plan },
    });

    return await this.request(request);
  }

  async purchaseSmsCredit(amount: number): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/purchaseSmsCredit`,
      data: { amount },
    });

    return await this.request(request);
  }
}
